import { dashboardApi } from '@/services/dashboardApi';
import { BACKEND_URL } from '@/config/api';

// FHC Academy y recursos de marketing (backend: routes/academy.py, prefijo /api/dashboard/academy)
const get = (path) => dashboardApi._fetch(`/academy${path}`);
const send = (method, path, body) => dashboardApi._fetch(`/academy${path}`, {
  method,
  ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
});
const enc = encodeURIComponent;
// FastAPI devuelve `detail` como array en los errores de validación (422)
const errMsg = (d) => (Array.isArray(d) ? d.map((x) => x?.msg).filter(Boolean).join(' · ') : d);

export const academyApi = {
  // Miembro
  courses: () => get('/courses'),
  course: (id) => get(`/courses/${enc(id)}`),
  completeLesson: (lessonId, completed = true) => send('POST', `/lessons/${enc(lessonId)}/complete`, { completed }),
  lessonViewed: (lessonId) => send('POST', `/lessons/${enc(lessonId)}/viewed`),
  continueLearning: () => get('/continue'),
  resources: () => get('/resources'),
  quiz: (courseId, quizId) => get(`/courses/${enc(courseId)}/quiz/${enc(quizId)}`),
  submitQuiz: (courseId, quizId, answers) => send('POST', `/courses/${enc(courseId)}/quiz/${enc(quizId)}`, { answers }),
  myCertificates: () => get('/certificates/mine'),
  // Cuaderno de trabajo (PDF) de un módulo: se descarga con la sesión del panel
  downloadWorkbook: async (courseId, moduleId, filename = 'cuaderno.pdf') => {
    const token = sessionStorage.getItem('dashboard_token');
    const res = await fetch(`${BACKEND_URL}/api/dashboard/academy/courses/${enc(courseId)}/modules/${enc(moduleId)}/workbook`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(errMsg(err.detail) || 'No se ha podido descargar el cuaderno');
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  },
  certificate: (code) => get(`/certificates/${enc(code)}`),
  // Verificación pública (sin sesión): lo que se consulta con el código impreso en el diploma
  verifyCertificate: async (code) => {
    const res = await fetch(`${BACKEND_URL}/api/public/academy/certificates/${enc(code)}`);
    if (!res.ok) throw new Error('No hemos podido comprobar el diploma');
    return res.json();
  },

  // Admin
  adminCourses: () => get('/admin/courses'),
  adminCreateCourse: (data) => send('POST', '/admin/courses', data),
  adminUpdateCourse: (id, data) => send('PUT', `/admin/courses/${enc(id)}`, data),
  adminDeleteCourse: (id) => send('DELETE', `/admin/courses/${enc(id)}`),
  adminImportFhca: () => send('POST', '/admin/courses/import-fhca'),
  adminCertificates: () => get('/admin/certificates'),
  adminUploadFile: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = sessionStorage.getItem('dashboard_token');
    const res = await fetch(`${BACKEND_URL}/api/dashboard/academy/admin/files`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(errMsg(data.detail) || 'No se ha podido subir el archivo');
    return data;
  },
  adminFixCertificate: (code, fullName) => send('PATCH', `/admin/certificates/${enc(code)}`, { full_name: fullName }),
  adminResources: () => get('/admin/resources'),
  adminCreateResource: (data) => send('POST', '/admin/resources', data),
  adminUpdateResource: (id, data) => send('PUT', `/admin/resources/${enc(id)}`, data),
  adminDeleteResource: (id) => send('DELETE', `/admin/resources/${enc(id)}`),
};

export const PLAN_LABEL = { starter: 'Access', basica: 'PRO', premium: 'Premium' };

// La academia es una sola: el curso oficial con sus bloques 0-7 (backend/academy_fhca.py)
export const ACADEMY_COURSE_ID = 'fhca-tokenizacion-mercado-inmobiliario';

// «Bloque 3 · Formas de participar…» → { number: 3, name: 'Formas de participar…' }. Sin prefijo, el número es la posición.
export function blockParts(title, index) {
  const m = /^(?:Bloque|Módulo)\s+(\d+)\s*·\s*(.*)$/.exec(title || '');
  return m ? { number: Number(m[1]), name: m[2] || title } : { number: index, name: title || `Bloque ${index}` };
}

// Vista previa del vídeo en el editor (la URL definitiva la calcula el servidor)
export function videoEmbedPreview(url) {
  if (!url) return null;
  let u;
  try { u = new URL(url.trim()); } catch { return null; }
  if (u.protocol !== 'https:') return null;
  const host = u.hostname.toLowerCase();
  const parts = u.pathname.split('/').filter(Boolean);
  const yt = /^[A-Za-z0-9_-]{11}$/;
  if (['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be', 'www.youtu.be'].includes(host)) {
    let id = null;
    if (host.endsWith('youtu.be')) id = parts[0];
    else if (parts[0] === 'watch') id = u.searchParams.get('v');
    else if (['embed', 'shorts', 'live', 'v'].includes(parts[0])) id = parts[1];
    return id && yt.test(id) ? `https://www.youtube-nocookie.com/embed/${id}` : null;
  }
  if (['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'].includes(host)) {
    let id = null; let h = u.searchParams.get('h');
    if (host === 'player.vimeo.com') { if (parts[0] === 'video') id = parts[1]; } else {
      const i = parts.findIndex((p) => /^\d{1,12}$/.test(p));
      if (i >= 0) { id = parts[i]; if (!h && parts[i + 1] && /^[A-Za-z0-9]{1,40}$/.test(parts[i + 1])) h = parts[i + 1]; }
    }
    if (!id || !/^\d{1,12}$/.test(id)) return null;
    return `https://player.vimeo.com/video/${id}${h && /^[A-Za-z0-9]{1,40}$/.test(h) ? `?h=${h}` : ''}`;
  }
  return null;
}

export const fmtMinutes = (min) => {
  const m = Math.max(0, Math.round(Number(min) || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60); const r = m % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
};
